/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AgentGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { ChangeEvent } from 'react';
import { User } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ExtensionSelect from '../../components/ui/ExtensionSelect';
import { FormState, AgentStatus, AgentStateMode, AgentType } from './types/agents.types';
import { STATUS_OPTIONS, STATE_MODE_OPTIONS, AGENT_TYPE_OPTIONS } from './constants';

interface AgentGeneralSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  onStatusChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onStateModeChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onAgentTypeChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export function AgentGeneralSection({
  formData,
  onFieldChange,
  onStatusChange,
  onStateModeChange,
  onAgentTypeChange
}: AgentGeneralSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('agents.form.sections.general')} icon={<User className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Agent Name */}
        <SimpleTooltip content={t('agents.form.fields.agentName.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentName" className="text-left">
            {t('agents.form.fields.agentName.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentName"
            value={formData.agentName}
            onChange={(event) => onFieldChange('agentName', event.target.value)}
            placeholder={t('agents.form.fields.agentName.placeholder')}
            required
          />
        </div>

        {/* Extension */}
        <SimpleTooltip content={t('agents.form.fields.extension.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="extension" className="text-left">
            {t('agents.form.fields.extension.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <ExtensionSelect
            value={formData.extension}
            onChange={(value) => onFieldChange('extension', value)}
            placeholder={t('agents.form.fields.extension.selectPlaceholder')}
            required
          />
        </div>

        {/* Initial Status */}
        <SimpleTooltip content={t('agents.form.fields.initialStatus.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="initialStatus" className="text-left">
            {t('agents.form.fields.initialStatus.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <select
            id="initialStatus"
            value={formData.initialStatus}
            onChange={onStatusChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {t(`agents.form.statuses.${status.replace(/[()]/g, '').replace(/\s/g, '')}`)}
              </option>
            ))}
          </select>
        </div>

        {/* State Mode */}
        <SimpleTooltip content={t('agents.form.fields.stateMode.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="stateMode" className="text-left">
            {t('agents.form.fields.stateMode.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <select
            id="stateMode"
            value={formData.stateMode}
            onChange={onStateModeChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STATE_MODE_OPTIONS.map((mode) => (
              <option key={mode} value={mode}>
                {t(`agents.form.stateModes.${mode}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Agent Type */}
        <SimpleTooltip content={t('agents.form.fields.agentType.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentType" className="text-left">
            {t('agents.form.fields.agentType.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <select
            id="agentType"
            value={formData.agentType}
            onChange={onAgentTypeChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {AGENT_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {t(`agents.form.agentTypes.${type}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Record Calls */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('agents.form.fields.recordCalls.tooltip')}>
            <Label htmlFor="recordCalls" className="text-left">
              {t('agents.form.fields.recordCalls.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="recordCalls"
            value={formData.recordCalls}
            onChange={(value) => onFieldChange('recordCalls', value)}
          />
        </div>

        {/* Max Concurrent Calls */}
        <SimpleTooltip content={t('agents.form.fields.maxConcurrentCalls.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxConcurrentCalls" className="text-left">
            {t('agents.form.fields.maxConcurrentCalls.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxConcurrentCalls"
            type="number"
            min="1"
            value={formData.maxConcurrentCalls}
            onChange={(event) => onFieldChange('maxConcurrentCalls', event.target.value)}
            placeholder={t('agents.form.fields.maxConcurrentCalls.placeholder')}
          />
        </div>

        {/* Enabled */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('agents.form.fields.enabled.tooltip')}>
            <Label htmlFor="enabled" className="text-left">
              {t('agents.form.fields.enabled.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="enabled"
            value={formData.enabled}
            onChange={(value) => onFieldChange('enabled', value)}
          />
        </div>
      </div>
    </FormBox>
  );
}

